import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import {
  VendorEventListItem,
  vendorGetEvents,
} from "../vendorApi";
import EventDetailDrawer from "./EventDetailDrawer";

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR EVENTS PAGE — Table with status filter + search
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  navy: "#0f172a",
  gold: "#d4a373",
  white: "#ffffff",
  bg: "#f1f5f9",
  slate: "#64748b",
  slateLt: "#94a3b8",
  line: "#e2e8f0",
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending:   { bg: "#fef3c7", color: "#92400e" },
  confirmed: { bg: "#dbeafe", color: "#1e40af" },
  completed: { bg: "#dcfce7", color: "#166534" },
};

const SERVICE_LABELS: Record<string, string> = {
  food:  "Food",
  bar:   "Bar",
  led:   "LED",
  addon: "Add-on",
};

const VendorEvents: React.FC = () => {
  const [events, setEvents] = useState<VendorEventListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await vendorGetEvents({
        status: statusFilter,
        search: search || undefined,
      });
      setEvents(data);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  /* Debounced search */
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const openDrawer = (id: number) => {
    setSelectedId(id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedId(null), 300);
  };

  const formatDate = (d: string) => {
    const date = new Date(d + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 26,
              fontWeight: 800,
              color: T.navy,
              letterSpacing: "-0.02em",
            }}
          >
            Events
          </Typography>
          <Typography sx={{ fontSize: 14, color: T.slate, mt: 0.3 }}>
            Manage your assigned events and pricing
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{
              minWidth: 130,
              borderRadius: "10px",
              fontSize: 13,
              bgcolor: T.white,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: T.line },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>

          <TextField
            placeholder="Search events..."
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 18, color: T.slateLt }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 220 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: 13,
                bgcolor: T.white,
                "& fieldset": { borderColor: T.line },
              },
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <Box
        sx={{
          bgcolor: T.white,
          borderRadius: "14px",
          border: `1px solid ${T.line}`,
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  "& .MuiTableCell-head": {
                    bgcolor: "#f8fafc",
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: T.slate,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    borderBottom: `1px solid ${T.line}`,
                    py: 1.5,
                  },
                }}
              >
                <TableCell>Event</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Hall</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ fontSize: 13, color: T.slateLt }}>
                      Loading events...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <EventOutlinedIcon
                      sx={{ fontSize: 40, color: T.slateLt, opacity: 0.4, mb: 1 }}
                    />
                    <Typography sx={{ fontSize: 14, color: T.slateLt }}>
                      No events found
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: T.slateLt, mt: 0.3 }}>
                      Events will appear here when assigned to you
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((ev) => (
                  <TableRow
                    key={ev.id}
                    onClick={() => openDrawer(ev.id)}
                    sx={{
                      cursor: "pointer",
                      transition: "background 0.12s",
                      "&:hover": { bgcolor: "#fafbfd" },
                      "& .MuiTableCell-root": {
                        borderBottom: `1px solid ${T.line}`,
                        py: 1.8,
                        fontSize: 13,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}
                      >
                        {ev.event_name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: T.slateLt }}>
                        {ev.booking_ref}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 500, color: T.navy }}
                      >
                        {ev.customer_name}
                      </Typography>
                      {ev.customer_org && (
                        <Typography sx={{ fontSize: 11, color: T.slateLt }}>
                          {ev.customer_org}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12.5, color: T.navy }}>
                        {formatDate(ev.event_date)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: T.slateLt }}>
                        {ev.event_time}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 12.5, color: T.navy }}>
                        {ev.hall_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={SERVICE_LABELS[ev.service_type] || ev.service_type}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 10.5,
                          fontWeight: 600,
                          bgcolor: "#f1f5f9",
                          color: T.navy,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ev.status.charAt(0).toUpperCase() + ev.status.slice(1)}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: STATUS_COLORS[ev.status]?.bg || "#f1f5f9",
                          color: STATUS_COLORS[ev.status]?.color || T.slate,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Detail Drawer */}
      <EventDetailDrawer
        open={drawerOpen}
        eventId={selectedId}
        onClose={closeDrawer}
        onPricingUpdated={fetchEvents}
      />
    </Box>
  );
};

export default VendorEvents;
